import { Calendar, CalendarClock, Landmark } from "lucide-react";
import { getRealmLabel } from "@/lib/integrations/quickbooks/realms";
import type { Recurring_Charges } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact, HeaderSubtitleSegment } from "../model";
import { formatHeaderDate } from "../model";

/** Guarded against GetRecurringCharge by detail-header-model-coverage.test.ts. */
export const RECURRING_CHARGE_HEADER_FIELDS = [
  "schedule_type",
  "next_charge_date",
  "last_charged_at",
  "realm_id",
] as const satisfies readonly (keyof Recurring_Charges)[];

export type RecurringChargeHeaderData = Pick<
  Recurring_Charges,
  (typeof RECURRING_CHARGE_HEADER_FIELDS)[number]
>;

/**
 * 1:1 scalar columns edited in the header. Recurring charges expose none today
 * — `next_charge_date` / `last_charged_at` are schedule-derived (read-only) and
 * `realm_id` / `schedule_type` are set at creation — so the header stays
 * read-only and nothing is dropped from the inline panel.
 */
export const RECURRING_CHARGE_HEADER_EDITABLE_FIELDS =
  [] as const satisfies readonly (keyof Recurring_Charges)[];

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function buildRecurringChargeHeaderModel(
  charge: RecurringChargeHeaderData
): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (charge.schedule_type) {
    subtitleSegments.push({ text: `${capitalize(charge.schedule_type)} schedule` });
  }

  const facts: HeaderFact[] = [];
  const nextCharge = formatHeaderDate(charge.next_charge_date);
  if (nextCharge) {
    facts.push({ id: "next-charge", icon: CalendarClock, label: "Next charge", value: nextCharge });
  }
  const lastCharged = formatHeaderDate(charge.last_charged_at);
  if (lastCharged) {
    facts.push({ id: "last-charged", icon: Calendar, label: "Last charged", value: lastCharged });
  }
  if (charge.realm_id) {
    // Two distinct QB companies (QUICKBOOKS_REALMS) — always label which one bills.
    facts.push({
      id: "qb-realm",
      icon: Landmark,
      label: "QuickBooks",
      value: getRealmLabel(charge.realm_id),
    });
  }

  return { subtitleSegments, facts, metrics: [] };
}
